import { icon } from "../iconsKit";
import { motionOk } from "../kit";
import type { ArmouryCtx } from "./host";
import { btn, h } from "./itemBits";

/**
 * The Armoury's item panels (picker, item card, Clear junk): each mounts as a CHILD of the Armoury sheet over its own
 * scrim (SheetGuard closes sibling `.sheet`s), stacks above the one it was opened from, and closes by ×, the scrim or a
 * swipe down its handle.
 */

export interface Panel {
  readonly root: HTMLDivElement;
  /** Removes the panel (and its scrim). */
  close(): void;
  /** Puts the panel back if a page refresh took it out of the sheet. */
  keep(): void;
  closed(): boolean;
}

/** Stacked panels (a card opened from the picker) sit above the one below; the first sits above the page. */
const PANEL_Z0 = 20;
/** A drag down the panel's handle further than this (px) dismisses it. */
export const SWIPE_CLOSE_PX = 72;

/**
 * Mounts a panel as a child of the Armoury sheet over a scrim (tap the scrim or × to close). `kind` panels replace
 * any open panel of the same kind; `onClose` runs once.
 */
export function mountPanel(ctx: ArmouryCtx, kind: string, onClose?: () => void): Panel {
  for (const old of Array.from(ctx.sheet.querySelectorAll<HTMLElement>(`.apanel.${kind}`))) (old as HTMLElement & { _close?: () => void })._close?.();
  const scrim = h("div", "ascrim");
  const root = h("div", `apanel ${kind}`);
  root.setAttribute("role", "dialog");
  const z = PANEL_Z0 + 2 * (ctx.sheet.querySelectorAll(".apanel").length + 1);
  scrim.style.zIndex = String(z);
  root.style.zIndex = String(z + 1);
  let isClosed = false;
  const close = () => {
    if (isClosed) return;
    isClosed = true;
    scrim.remove();
    root.remove();
    onClose?.();
  };
  (root as HTMLElement & { _close?: () => void })._close = close;
  scrim.addEventListener("click", close);
  ctx.sheet.append(scrim, root);
  if (motionOk()) root.classList.add("enter");
  return {
    root, close, closed: () => isClosed,
    keep: () => { if (!isClosed && root.parentElement !== ctx.sheet) ctx.sheet.append(scrim, root); },
  };
}

/** Re-renders every open item panel (after a change made in another one, e.g. an upgrade on a card over the picker). */
export function refreshPanels(ctx: ArmouryCtx, except?: HTMLElement): void {
  for (const p of Array.from(ctx.sheet.querySelectorAll<HTMLElement>(".apanel"))) if (p !== except) (p as HTMLElement & { _refresh?: () => void })._refresh?.();
}

/** Closes every open item panel (an equip from the card sends the player back to the doll). */
export function closePanels(ctx: ArmouryCtx): void {
  for (const p of Array.from(ctx.sheet.querySelectorAll<HTMLElement>(".apanel"))) (p as HTMLElement & { _close?: () => void })._close?.();
}

/** Lets `refreshPanels` re-render this panel. */
export function onRefresh(panel: Panel, fn: () => void): void { (panel.root as HTMLElement & { _refresh?: () => void })._refresh = fn; }

/** Drag `handle` down to dismiss the panel (the sheet idiom); the panel follows the finger (transform only). */
export function swipeToClose(panel: Panel, handle: HTMLElement): void {
  let y0: number | null = null, dy = 0;
  const end = () => {
    if (y0 === null) return;
    y0 = null;
    if (dy > SWIPE_CLOSE_PX) panel.close();
    else panel.root.style.transform = "";
    dy = 0;
  };
  handle.addEventListener("pointerdown", (e: Event) => { y0 = (e as PointerEvent).clientY ?? 0; dy = 0; });
  handle.addEventListener("pointermove", (e: Event) => {
    if (y0 === null) return;
    dy = Math.max(0, ((e as PointerEvent).clientY ?? y0) - y0);
    panel.root.style.transform = dy > 0 ? `translateY(${dy}px)` : "";
  });
  handle.addEventListener("pointerup", end);
  handle.addEventListener("pointercancel", () => { dy = 0; end(); });
}

export function closeButton(onTap: () => void): HTMLButtonElement {
  const b = btn("icbtn aclose");
  b.setAttribute("aria-label", "Close");
  b.innerHTML = icon("close", 20);
  b.addEventListener("click", (e) => { e.stopPropagation(); onTap(); });
  return b;
}

